<script lang="ts">
  import ArrowUpRight from '@lucide/svelte/icons/arrow-up-right';
  import MotionToggle from '$lib/components/site/motion-toggle.svelte';
  import { CONTACT_PATH, SIGN_IN_URL, SITE_PAGES, SUPPORT_EMAIL, absoluteUrl } from '$lib/utils/site';
  import { scrollToContactForm } from '$lib/utils/contact-scroll';

  const year = new Date().getFullYear();
  const helpUrl = absoluteUrl('/help');
  const explore = [
    { label: 'The platform', href: SITE_PAGES.product.path },
    { label: 'For institutions', href: SITE_PAGES.institutions.path },
    { label: 'Pricing', href: SITE_PAGES.pricing.path },
    { label: 'Questions', href: '/#faq' },
    { label: 'Help centre', href: helpUrl }
  ];

  function openContact(event: MouseEvent) {
    if (!scrollToContactForm()) return;

    event.preventDefault();
  }
</script>

<footer class="site-footer">
  <div class="footer-top section-width">
    <div class="footer-brand">
      <a href="/" aria-label="Edvello home"
        ><img src="/brand/edvello-logo-horizontal-dark.svg" width="160" height="44" alt="Edvello" loading="lazy" /></a
      >
      <p>A place for your teaching.<br />A way forward for every learner.</p>
      <a href={CONTACT_PATH} class="footer-cta" onclick={openContact}
        >Get started <span><ArrowUpRight size={16} /></span></a
      >
    </div>
    <nav aria-label="Footer product links">
      <h2>Explore</h2>
      {#each explore as link}<a href={link.href}>{link.label}</a>{/each}
    </nav>
    <nav aria-label="Footer contact links">
      <h2>Get in touch</h2>
      <a href={CONTACT_PATH}>Contact us</a><a href={SIGN_IN_URL}>Sign in</a><a href="mailto:{SUPPORT_EMAIL}"
        >{SUPPORT_EMAIL}</a
      >
    </nav>
  </div>
  <div class="footer-wordmark section-width" aria-hidden="true">Edvello</div>
  <div class="footer-bottom section-width">
    <span>© {year} Edvello</span>
    <div>
      <a href={SITE_PAGES.privacy.path}>Privacy</a><a href={SITE_PAGES.terms.path}>Terms</a><span class="footer-motion"
        ><MotionToggle variant="inline" /></span
      >
    </div>
  </div>
</footer>

<style>
  .site-footer {
    --ed-muted: #bccae0;
    --ed-line: #2e405f;
    --ed-accent: #f4edac;
    position: relative;
    overflow: hidden;
    color: #f0f5ff;
    background: radial-gradient(ellipse 60% 50% at 85% 0%, #1447e62e, transparent 70%), #101f3a;
    padding: 80px 0 28px;
  }
  .footer-top {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    gap: 60px;
    padding-bottom: 40px;
  }
  .footer-brand img {
    width: 160px;
    height: auto;
  }
  .footer-brand p {
    color: var(--ed-muted);
    line-height: 1.6;
    margin: 22px 0 26px;
  }
  .footer-cta {
    display: inline-flex;
    align-items: center;
    gap: 14px;
    padding: 6px 6px 6px 18px;
    border-radius: 10px;
    background: #1447e6;
    color: #fff;
    font-size: 13px;
    font-weight: 550;
    transition: background 250ms;
  }
  .footer-cta span {
    display: grid;
    place-items: center;
    width: 30px;
    height: 30px;
    border-radius: 7px;
    background: #f4edac;
    color: #17315d;
    transition: transform 400ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  .footer-cta:hover {
    background: #2354e9;
  }
  .footer-cta:hover span {
    transform: rotate(45deg);
  }
  nav {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  nav h2 {
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 8px;
  }
  nav a {
    position: relative;
    font-size: 14px;
    color: var(--ed-muted);
    transition: color 200ms;
  }
  nav a:hover {
    color: var(--ed-accent);
  }
  .footer-wordmark {
    font-size: clamp(90px, 19vw, 260px);
    font-weight: 600;
    line-height: 0.8;
    letter-spacing: -0.06em;
    text-align: center;
    color: transparent;
    background: linear-gradient(180deg, #1c3360, #101f3a 92%);
    background-clip: text;
    -webkit-background-clip: text;
    user-select: none;
    margin-bottom: -6px;
  }
  .footer-bottom {
    position: relative;
    border-top: 1px solid var(--ed-line);
    padding: 24px 64px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    font-size: 12px;
    color: var(--ed-muted);
  }
  .footer-bottom div {
    display: flex;
    align-items: center;
    gap: 24px;
  }
  .footer-bottom a:hover {
    color: var(--ed-accent);
  }
  .footer-motion {
    display: none;
  }
  @media (max-width: 760px) {
    .site-footer {
      padding: 48px 0 22px;
    }
    .footer-top {
      grid-template-columns: 1fr 1fr;
      gap: 28px 20px;
      padding-bottom: 24px;
    }
    .footer-brand {
      grid-column: 1/-1;
    }
    .footer-brand img {
      width: 135px;
    }
    .footer-brand p {
      font-size: 14px;
      line-height: 1.65;
      margin: 16px 0 20px;
    }
    nav {
      gap: 0;
    }
    nav h2 {
      font-size: 13px;
      margin-bottom: 5px;
    }
    nav a {
      min-height: 44px;
      display: flex;
      align-items: center;
      font-size: 13px;
      line-height: 1.5;
      overflow-wrap: anywhere;
    }
    .footer-bottom {
      flex-wrap: wrap;
      padding: 14px 56px 0 0;
      gap: 6px 12px;
      font-size: 11px;
    }
    .footer-bottom div {
      gap: 18px;
    }
    .footer-bottom a {
      min-height: 44px;
      display: inline-flex;
      align-items: center;
    }
    .footer-motion {
      display: inline-flex;
      color: var(--ed-muted);
    }
  }
</style>
